import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";
import { Lock, Search, Shield, UserX, UserCheck, Save, History, X } from "lucide-react";

const PLANS = ["free", "basic", "pro", "agency"];

export default function AdminUsers() {
  const tr = useTr();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null); // user being edited in modal
  const [txs, setTxs] = useState([]);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
  };

  useEffect(() => { if (isAdmin) load(); else setErr("Requiere permisos de administrador"); }, [isAdmin]);

  const openEdit = async (u) => {
    setEdit({ ...u, _newPlan: u.plan, _newRole: u.role, _newActive: u.active !== false });
    setTxs([]);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/transactions`);
      setTxs(data);
    } catch {}
  };

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const body = {};
      if (edit._newPlan !== edit.plan) body.plan = edit._newPlan;
      if (edit._newRole !== edit.role) body.role = edit._newRole;
      if (edit._newActive !== (edit.active !== false)) body.active = edit._newActive;
      if (!Object.keys(body).length) { toast.info("Sin cambios"); setSaving(false); return; }
      await api.patch(`/admin/users/${edit.id}`, body);
      toast.success("Usuario actualizado");
      setEdit(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="admin-users-denied">
        <Lock className="w-8 h-8 mx-auto text-[#E53935]" />
        <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-3">ACCESO DENEGADO</div>
        <div className="font-display text-xl mt-2">{err}</div>
      </div>
    );
  }

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADMIN · GESTIÓN DE USUARIOS")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Usuarios")}</h1>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr("Buscar por email o nombre…")}
            className="w-full bg-white border border-[#0A0A0A] pl-10 pr-4 py-3 font-mono-data text-sm"
            data-testid="admin-users-search"
          />
        </div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
          {filtered.length} / {users.length}
        </div>
      </div>

      <div className="bg-white border border-[#0A0A0A] overflow-x-auto">
        <table className="w-full text-sm font-mono-data">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">EMAIL</th>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">NOMBRE</th>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">PLAN</th>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">ROL</th>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">ESTADO</th>
              <th className="text-left px-3 py-3 font-mono-mark text-[10px]">CREADO</th>
              <th className="text-right px-3 py-3 font-mono-mark text-[10px]">ACCIÓN</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const active = u.active !== false;
              return (
                <tr key={u.id} className="border-t border-[#0A0A0A]" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-3 truncate max-w-[220px]">{u.email}</td>
                  <td className="px-3 py-3 truncate max-w-[160px]">{u.name || "—"}</td>
                  <td className="px-3 py-3 uppercase">{u.plan}</td>
                  <td className="px-3 py-3">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 font-mono-mark text-[9px] bg-[#E53935] text-white px-2 py-0.5">
                        <Shield className="w-3 h-3" /> ADMIN
                      </span>
                    ) : (
                      "user"
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {active ? (
                      <span className="font-mono-mark text-[9px] bg-[#FFD600] px-2 py-0.5">ACTIVO</span>
                    ) : (
                      <span className="font-mono-mark text-[9px] bg-[#E5E5E5] px-2 py-0.5">SUSPENDIDO</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#5C5C5C]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      onClick={() => openEdit(u)}
                      className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-3 py-1.5 border border-[#0A0A0A] hover-brutal"
                      data-testid={`admin-user-edit-${u.id}`}
                    >
                      EDITAR
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white border border-[#0A0A0A] brutal-shadow max-w-2xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-user-edit-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">EDITAR USUARIO</div>
                <div className="font-display text-2xl mt-1 break-all">{edit.email}</div>
              </div>
              <button onClick={() => setEdit(null)} className="text-[#5C5C5C] hover:text-[#E53935]" data-testid="admin-user-edit-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">PLAN</span>
                <select
                  value={edit._newPlan}
                  onChange={(e) => setEdit({ ...edit, _newPlan: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                  data-testid="admin-user-plan-select"
                >
                  {PLANS.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">ROL</span>
                <select
                  value={edit._newRole}
                  onChange={(e) => setEdit({ ...edit, _newRole: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                  data-testid="admin-user-role-select"
                >
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </label>
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">ESTADO</span>
                <button
                  onClick={() => setEdit({ ...edit, _newActive: !edit._newActive })}
                  className={`w-full mt-2 px-3 py-2 font-mono-mark text-xs flex items-center justify-center gap-2 border ${edit._newActive ? "bg-[#FFD600] border-[#0A0A0A]" : "bg-[#E53935] text-white border-[#E53935]"}`}
                  data-testid="admin-user-active-toggle"
                >
                  {edit._newActive ? <><UserCheck className="w-4 h-4" /> ACTIVO</> : <><UserX className="w-4 h-4" /> SUSPENDIDO</>}
                </button>
              </label>
            </div>

            <div className="border-t border-[#0A0A0A] pt-4">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-3 flex items-center gap-2">
                <History className="w-3 h-3" /> HISTORIAL DE PAGOS
              </div>
              {txs.length === 0 ? (
                <div className="text-sm font-mono-data text-[#5C5C5C]">{tr("Sin pagos registrados.")}</div>
              ) : (
                <table className="w-full text-sm font-mono-data">
                  <thead>
                    <tr className="border-b border-[#0A0A0A]">
                      <th className="text-left py-2 font-mono-mark text-[9px]">FECHA</th>
                      <th className="text-left py-2 font-mono-mark text-[9px]">PLAN</th>
                      <th className="text-right py-2 font-mono-mark text-[9px]">MONTO</th>
                      <th className="text-right py-2 font-mono-mark text-[9px]">ESTADO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txs.map((t, i) => (
                      <tr key={i} className="border-t border-[#0A0A0A]/20">
                        <td className="py-2">{t.updated_at ? new Date(t.updated_at).toLocaleDateString("es-MX") : "—"}</td>
                        <td className="py-2 uppercase">{t.plan_id}</td>
                        <td className="py-2 text-right">${t.amount}</td>
                        <td className="py-2 text-right">
                          <span className={`font-mono-mark text-[9px] px-2 py-0.5 ${t.payment_status === "paid" ? "bg-[#FFD600]" : "bg-[#E5E5E5]"}`}>
                            {(t.payment_status || "pending").toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#0A0A0A]">
              <button onClick={() => setEdit(null)} className="font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-4 py-2">
                CANCELAR
              </button>
              <button onClick={save} disabled={saving} className="font-mono-mark text-[10px] bg-[#E53935] text-white px-4 py-2 border border-[#E53935] disabled:opacity-50 flex items-center gap-2" data-testid="admin-user-save">
                <Save className="w-3 h-3" /> {saving ? "GUARDANDO…" : "GUARDAR CAMBIOS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
